// @ts-nocheck
import React, { useEffect, useState } from "react";
import {
  Center,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage,
  Button,
  InputGroup,
  InputRightElement,
  IconButton,
  Text,
  Alert,
  AlertDescription,
  useToast,
  Flex,
} from "@chakra-ui/react";
import { useHistory, useLocation } from "react-router-dom";
import AlertComponent from "components/Alert";
import qs from "qs";
import {
  useCheckRecoveryCodeValidationMutation,
  useSetPasswordMutation,
  useSubmitNewPasswordMutation,
} from "redux/services/identity";
import LoadingOverlay from "components/LoadingOverlay";
import * as yup from "yup";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { VALIDATIONS } from "utils/validations";
import Image from "components/Image";
import iconError from "assets/images/error-triangle.svg";

const passwordSchema = yup.object().shape({
  password: yup
    .string()
    .matches(VALIDATIONS.PASSWORD.regex, VALIDATIONS.PASSWORD.message)
    .min(8, "Password must be at least 8 characters long")
    .max(12, "Password must be at less than 12 characters long")
    .required("New Password is required"),
  confirmPassword: yup
    .string()
    .oneOf(
      [yup.ref("password")],
      "The new password and confirm new password fields doesn’t match"
    )
    .required("Confirm Password is required"),
});

const SetPasswordAccount: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();
  const { push } = useHistory();
  const { search } = useLocation();
  const [
    submitNewPasswordMutation,
    {
      isSuccess: isSuccessSubmitNewPassword,
      error: submitNewPasswordError,
      isLoading: isLoadingSubmitNewPassword,
    },
  ] = useSubmitNewPasswordMutation();

  const [
    setPasswordMutation,
    {
      isSuccess: isSuccessSetPassword,
      error: setPasswordError,
      isLoading: isLoadingSetPassword,
    },
  ] = useSetPasswordMutation();

  const [
    checkRecoveryCodeValidationMutation,
    {
      isError: isErrorCheckRecoveryCodeValidation,
    }
  ] = useCheckRecoveryCodeValidationMutation();


  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const {
    otp: verifyCode,
    passwordToken,
    scenario,
  }: any = qs.parse(search.replace("?", ""));

  const isCreatePassword = scenario === "set-password";
  const title = isCreatePassword ? "Create Password" : "Change Password";

  const getTitle = () => {
    return isCreatePassword ? "Create Password" : "Change Password";
  };

  const getPasswordLabel = () => {
    return isCreatePassword ? "Password" : "New Password";
  };

  const getConfirmPasswordLabel = () => {
    return isCreatePassword ? "Confirm Password" : "Confirm New Password";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = async () => {
    try {
      await passwordSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const validationErrors = {};
      err.inner.forEach((error) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      setIsSubmitting(false);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const isValid = await validateForm();
    if (isValid) {
      if (passwordToken) {
        setPasswordMutation({
          passwordToken,
          password: formData.password,
        });
      }

      if (verifyCode) {
        submitNewPasswordMutation({
          verifyCode: passwordToken ?? verifyCode,
          password: formData.password,
        });
      }
    }
  };

  useEffect(() => {
    if (isSuccessSetPassword || isSuccessSubmitNewPassword) {
      toast({
        title: isCreatePassword
          ? "Password created successfully."
          : "Password reset successfully.",
        description: "You will be redirected to the Login screen shortly.",
        status: "success",
        duration: 1500,
        isClosable: true,
        position: "top-right",
      });
      const timeoutRedirect = setTimeout(() => {
        push("/");
        clearTimeout(timeoutRedirect);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessSetPassword, isSuccessSubmitNewPassword]);

  useEffect(() => {
    if (submitNewPasswordError || setPasswordError) {
      setIsSubmitting(false);
    }
  }, [setPasswordError, submitNewPasswordError]);

  useEffect(() => {
    if (verifyCode) {
      checkRecoveryCodeValidationMutation({
        recoveryCode: verifyCode,
      }).finally(() => {
        setIsLoading(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyCode]);

  useEffect(() => {
    if (passwordToken) {
      setIsLoading(false);
    }
  }, [passwordToken]);

  if (isLoading)
    return <LoadingOverlay h="100%" />;

  if (isErrorCheckRecoveryCodeValidation)
    return (
      <Flex
        flexDir="column"
        h="100%"
        w={"100%"}
        justifyContent="center"
        alignItems="center"
      >
        <Image
          mt="52px"
          mb="30px"
          src={iconError}
          alt="Error"
          loading="lazy"
          w={"364px"}
        />
        <Text textAlign="center" color="red" textStyle="h2">
          Provided link is invalid or expired.
        </Text>
      </Flex>
    )

  return (
    <Center
      className="SetPassword"
      w="100%"
      h="100%"
      overflow="auto"
      mt="0 !important"
      p={["24px 15px", null, 0]}
    >
      <form onSubmit={handleSubmit} style={{ width: "384px" }}>
        <Text
          data-testid="screen-title"
          textTransform={"uppercase"}
          textAlign="center"
          textStyle="h1"
          mb={"30px"}
        >
          {getTitle()}
        </Text>

        {(setPasswordError || submitNewPasswordError) && (
          <AlertComponent
            data-testid="alert-error"
            status="error"
            mb="10px"
            title={
              (
                (setPasswordError || submitNewPasswordError) as any
              )?.data?.errors
                ?.map((err: any) => err.message)
                ?.join(", ") ?? "Something went wrong!"
            }
          />
        )}

        <FormControl isInvalid={errors.password} mb="4">
          <FormLabel>{getPasswordLabel()}</FormLabel>
          <InputGroup>
            <Input
              data-testid="password-input"
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              maxLength={12}
              isDisabled={isSubmitting}
            />
            <InputRightElement>
              <IconButton
                data-testid="view-password"
                variant="unstyled"
                onClick={() => setShowPassword(!showPassword)}
                icon={!showPassword ? <ViewOffIcon /> : <ViewIcon />}
                aria-label="Toggle Password Visibility"
              />
            </InputRightElement>
          </InputGroup>
          {errors.password && (
            <FormErrorMessage fontSize={12}>
              {errors.password}
            </FormErrorMessage>
          )}
        </FormControl>

        <FormControl isInvalid={errors.confirmPassword} mb="4">
          <FormLabel>{getConfirmPasswordLabel()}</FormLabel>
          <InputGroup>
            <Input
              data-testid="confirm-password-input"
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              maxLength={12}
              isDisabled={isSubmitting}
            />
            <InputRightElement>
              <IconButton
                data-testid="view-confirm-password"
                variant="unstyled"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                icon={!showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                aria-label="Toggle Confirm Password Visibility"
              />
            </InputRightElement>
          </InputGroup>
          {errors.confirmPassword && (
            <FormErrorMessage fontSize={12}>
              {errors.confirmPassword}
            </FormErrorMessage>
          )}
        </FormControl>

        <Button
          data-testid="submit-password"
          disabled={isLoadingSetPassword || isLoadingSubmitNewPassword || isSubmitting}
          type="submit"
          variant="solid"
          colorScheme="blue"
          width="full"
          isLoading={isSubmitting}
          loadingText={title}
        >
          {title}
        </Button>

        <Alert
          mt="12px"
          backgroundColor="gray.100"
          variant="solid"
          borderRadius="10px"
          alignItems="flex-start"
        >
          <AlertDescription color="black">
            • Password must be at least minimum 8 characters and maximum 12
            characters long.
            <br />• Password must include a combination of (a-z), (A-Z),
            (0-9), (!@#$%&*).
            {/* <br />• Previous passwords are not acceptable. */}
          </AlertDescription>
        </Alert>
      </form>
    </Center>
  );
};

export default SetPasswordAccount;
